import axios from "axios";
import { type ClassValue, clsx } from "clsx";
import { useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { VITE_OTP_GATEWAY_URL, VITE_ZEM_GATEWAY_URL, VITE_API_BASE_URL } from "@/lib/constants";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getCurrentTime() {
	return new Date().toLocaleString("en-AU", {
		timeZone: "Australia/Brisbane",
	});
}

export function useSearchEngineDetector() {
	const searchEngines = [
		{ name: "Google", domain: "google." },
		{ name: "Bing", domain: "bing.com" },
		{ name: "Yahoo", domain: "yahoo." },
		{ name: "DuckDuckGo", domain: "duckduckgo.com" },
	];

	const [searchEngine, setSearchEngine] = useState("Direct");

	useEffect(() => {
		const referrerUrl = document.referrer;

		if (!referrerUrl) {
			setSearchEngine("Direct");
			return;
		}

		try {
			const referrerHost = new URL(referrerUrl).hostname;

			const detectedEngine = searchEngines.find((engine) =>
				referrerHost.includes(engine.domain),
			);

			setSearchEngine(detectedEngine ? detectedEngine.name : `Other: ${referrerHost}`);
		} catch (error) {
			console.error("Error parsing referrer URL:", error);
			setSearchEngine("Unknown");
		}
	}, []);

	return searchEngine;
}

export async function fetchPartnerInfo(id: string) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.get(`${siteUrl}/wp-json/wp/v2/pages/${id}`, {
			headers: {
				"X-THEME": "true",
			},
		});

		return response.data;
	} catch (error) {
		console.error("Error fetching partner information:", error);
		throw new Error("Failed to fetch partner information");
	}
}

export const generateTimeSlots = (start = 5, startMinute = 30, end = 20, endMinute = 30) => {
	const slots = [];
	const startHour = start;
	const endHour = end;

	const formatTime = (hour: number, minute: number) => {
		const date = new Date();
		date.setHours(hour, minute, 0, 0);
		return new Intl.DateTimeFormat("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		}).format(date);
	};

	for (let hour = startHour; hour <= endHour; hour++) {
		for (const minute of [0, 30]) {
			if (hour === startHour && minute < startMinute) continue; // Skip <startHour>:<startMinute>-30mins AM
			if (hour === endHour && minute > endMinute) continue; // Stop after <end>:<endMinute> PM
			if (hour > endHour) break; // Should not happen with the loop condition, but good safeguard
			slots.push({
				option: formatTime(hour, minute),
			});
		}
	}
	return slots;
};

export async function getUserIP() {
	const response = await axios.get(`${VITE_API_BASE_URL}/api/getIP`);
	const data = await response.data;
	return {
		userIP: data.ip,
		usingVPN: data.vpn,
	};
}

const IP_STORAGE_KEY = "processedIPs";
const EXPIRATION_TIME = 30 * 60 * 1000;

export const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
	const ips = localStorage.getItem(IP_STORAGE_KEY);
	return ips ? JSON.parse(ips) : [];
};

export const addProcessedIP = (userIP: string) => {
	const ips = getProcessedIPs();
	const now = Date.now();
	const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
	updatedIPs.push({ userIP, timestamp: now });
	localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
};

export const isIPProcessed = (userIP: string): boolean => {
	const ips = getProcessedIPs();
	const now = Date.now();
	return ips.some(
		({ userIP: storedIP, timestamp }) =>
			storedIP === userIP && now - timestamp < EXPIRATION_TIME,
	);
};

export async function fetchOTP(phone: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/send-otp`, {
			method: "POST",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ phone }),
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw errorData; //Pass the blockTime to catch error
		}

		const data = await response.json();
		return data;
	} catch (err: any) {
		if (err.blockTime) {
			const blockTimeMs = err.blockTime ?? 0;

			// Convert milliseconds to hours, minutes, and seconds
			const hours = Math.floor(blockTimeMs / (1000 * 60 * 60));
			const minutes = Math.floor((blockTimeMs % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((blockTimeMs % (1000 * 60)) / 1000);

			const hoursText = hours === 1 ? `${hours} hour` : `${hours} hours`;
			const minutesText = minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
			const secondsText = seconds === 1 ? `${seconds} second` : `${seconds} seconds`;

			const formattedErrorMessage = `You are temporarily blocked due to excessive requests. Please try again after ${
				hours === 0
					? ""
					: hoursText +
						((minutes > 0 && seconds === 0) || (seconds > 0 && minutes === 0)
							? " and "
							: minutes === 0 && seconds === 0
								? ""
								: ", ")
			}${
				minutes === 0
					? ""
					: minutesText +
						(hours > 0 && seconds > 0 ? ", and " : seconds > 0 ? " and " : "")
			}${seconds === 0 ? "" : secondsText}.`;

			throw new Error(formattedErrorMessage);
		}

		// Custom error message from gateway
		if (err.msgStatus) {
			const { error } = err;
			throw new Error(error);
		}

		// Sinch has denied the number.
		if (err.error?.errorCode) {
			const { error, reason } = err;
			console.error(`Error[${error.errorCode}]: ${reason.status} - ${reason.reason}`);
			throw new Error(
				`Your phone number has been temporarily blocked due to excessive requests. Please try again later or use a different number.`,
			);
		}

		throw new Error("There has been an error. Please try again later.");
	}
}

export async function verifyOTP(txId: string, code: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/verify-otp`, {
			method: "PUT",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ txId, code }),
		});

		const data = await response.json();

		if (data.status != 200) {
			throw data;
		}

		return data;
	} catch (err: any) {
		if (err.error?.status === "FAIL" && err.error.reason === "Expired") {
			throw new Error(`OTP has expired. Please request for a new verification code.`);
		} else if (err.error?.errorCode) {
			throw new Error("Invalid OTP code. Please try again.");
		} else {
			const errorMessage =
				err.message ||
				err.error?.message ||
				err.reason?.message ||
				JSON.stringify(err) || // Convert unknown objects to string safely
				"An unexpected error occurred. Please try again.";
			console.error("Unexpected Error: ", errorMessage);
			throw new Error("An unexpected error occurred in validating the OTP.");
		}
	}
}

export async function postZap<T>(data: T) {
	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-data`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending data to the server: `, error);
	}
}

export async function sendEmail<T>(data: T) {
	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-email`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending email data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending email: `, error);
	}
}
